import { Component, inject, OnInit, signal } from '@angular/core';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { FolderItem } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { FolderService } from 'src/app/shared/services/folder.service';
import { RvshareAppDetectionService } from '../../services/rvshare-app-detection.service';
import { ScreenSharingService } from '../../services/screen-sharing.service';

@Component({
    selector: 'moderator-join',
    templateUrl: './moderator-join.component.html',
    styleUrl: './moderator-join.component.scss',
    standalone: false
})
export class ModeratorJoinComponent implements OnInit {

  loadingDisplays = signal(false);

  folders: FolderItem[] = [];
  availableDisplays: any[];
  tableColumns = ['name', 'status'];
  savingFavorite: string;

  search: any = {
    filter: 'licensed:=1&&screenSharingEnabled:=true&&screenSharingMode:=moderated',
    includeSubcompanies: false
  };
  filterConfig = {
    placeholder: 'Search Displays',
    borderless: true,
  };
  favoriteFilterConfig = {
    placeholder: 'Search Displays',
  };
  currentFolderId: string;
  folderService = inject(FolderService);

  constructor(
    private displayApiService: DisplayApiService,
    protected userStateService: UserStateService,
    protected rvshareAppDetectionService: RvshareAppDetectionService,
    protected screenSharingService: ScreenSharingService
  ) {

  }

  ngOnInit(): void {
    this.loadDisplays();
    this.loadFolders();
  }

  loadDisplays = () => {
    this.loadingDisplays.set(true);
    this.displayApiService.list(this.search).then((result) => {
      if (result.items && result.items.length > 0) {
        this.availableDisplays = this.sortDisplays(result.items);
      } else {
        this.availableDisplays = [];
      }
    }).finally(() => {
      this.loadingDisplays.set(false);
    });  
  };

  loadFolders() {
    this.folderService.loadFolders('Display', this.search).then((folders) => {
      this.folders = folders;
    });
  }

  get datasource() {
    if (this.search.query) {
      return this.availableDisplays;
    }
    const displays = this.availableDisplays ?? [];
    return [
      ...this.folders.filter((folder) => this.folderService.showFolderItem(
        this.currentFolderId || FoldersApiService.ROOT_FOLDER_ID,
        folder.parentId
      )),
      ...displays.filter((display) => 
        this.folderService.showFolderItem(
          this.currentFolderId || FoldersApiService.ROOT_FOLDER_ID,
          display.folderId
        )
      )
    ];
  }

  get favoritesDataSource() {
    const displays = this.availableDisplays ?? [];
    return [
      ...displays.filter((display) => this.isFavored(display) && (
        !this.search.query || display.name.toLowerCase().includes(this.search.query.toLowerCase())
      ))
    ];
  }

  searchFavorites = () => {};

  getDisplay(displayId: string) {
    return this.availableDisplays.find((display) => display.id === displayId);
  }

  isFavored(display: any): boolean {
    const userEmail = this.userStateService.getUserEmail();
    return !!(display?.favoredBy?.find((by) => by === userEmail));
  }

  sortDisplays(displayList: any[]): any[] {
    return displayList.filter((item) => this.isFavored(item))
      .concat(
        displayList.filter((item) => !this.isFavored(item)
      )
    );
  }

  private updateFavorite(display: any, email: string): void {
    if (this.isFavored(display)) {
      let index = display.favoredBy.indexOf(email);
      if (index >= 0) {
        display.favoredBy.splice(index, 1);
      }
    } else {
      display.favoredBy = display.favoredBy ?? [];
      display.favoredBy.push(email);
    }
  }

  toggleFavorite(displayId: string) {
    const display = this.getDisplay(displayId);
    const email = this.userStateService.getUserEmail();
    this.updateFavorite(display, email);

    this.savingFavorite = displayId;
    this.displayApiService
      .update(displayId, display)
      .catch((error) => {
        console.error(error);
        this.updateFavorite(display, email);
      }).finally(() => {
        this.savingFavorite = '';
        this.availableDisplays = this.sortDisplays(this.availableDisplays);
      });
  }

  onFolderClicked(folderId: string) {
    this.currentFolderId = folderId;
    this.search.folderId = folderId;
    this.loadDisplays();
  }

}
